// Markdown for answers, as an agent writes it (byj-1wv.51.4): headings,
// paragraphs, lists (nested, numbered, tasks), quotes, rules, GitHub's
// tables and fenced code; inline, code, bold, italics, strike-through and
// links. Built as React elements — the text is never parsed as HTML, so
// nothing an agent writes can inject markup — and a link goes only to an
// http, https or mailto address; any other is its text.
import { memo, useMemo, type ReactNode } from "react";

type Align = "left" | "center" | "right" | null;

export type Block =
  | { kind: "p"; text: string }
  | { kind: "h"; level: number; text: string }
  | { kind: "code"; lang: string; text: string }
  | { kind: "hr" }
  | { kind: "quote"; blocks: Block[] }
  | { kind: "list"; ordered: boolean; start: number; items: { task?: boolean; blocks: Block[] }[] }
  | { kind: "table"; align: Align[]; head: string[]; rows: string[][] };

const fenceRe = /^\s*(```+|~~~+)\s*([\w+-]*)\s*$/;
// a closing run of #s is dropped only after a space, as CommonMark does: "C#" keeps its #
const headingRe = /^\s{0,3}(#{1,6})\s+(.*?)(?:\s+#+)?\s*$/;
const ruleRe = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const quoteRe = /^\s{0,3}>\s?(.*)$/;
const itemRe = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
const tableSepRe = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/;

/**
 * A table's row as its cells, an escaped `\|` kept in its cell. Read a
 * character at a time rather than split on a lookbehind, which Safari before
 * 16.4 cannot parse — and this module is in the app's first bundle.
 */
function cells(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  const out: string[] = [];
  let cell = "";
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "\\" && s[i + 1] === "|") {
      cell += "|";
      i++;
    } else if (s[i] === "|") {
      out.push(cell.trim());
      cell = "";
    } else {
      cell += s[i];
    }
  }
  out.push(cell.trim());
  return out;
}

/**
 * A table's head and its separator: a line with a bar over one of dashes and
 * bars with as many cells — not a line that happens to hold a "|" over a
 * rule or a heading's underline.
 */
function isTable(line: string, next: string | undefined): boolean {
  return line.includes("|") && next !== undefined && next.includes("|") && next.includes("-") && tableSepRe.test(next) && cells(next).length === cells(line).length;
}

/** What ends a paragraph: a numbered item only from 1, as CommonMark has it, so "2019. was good" stays prose. */
function startsBlock(line: string, next: string | undefined): boolean {
  const item = itemRe.exec(line);
  return (
    fenceRe.test(line) ||
    headingRe.test(line) ||
    ruleRe.test(line) ||
    quoteRe.test(line) ||
    (item !== null && (!/\d/.test(item[2]!) || parseInt(item[2]!, 10) === 1)) ||
    isTable(line, next)
  );
}

export function blocks(src: string): Block[] {
  const lines = src.replace(/\r\n?/g, "\n").split("\n");
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === "") {
      i++;
      continue;
    }
    const fence = fenceRe.exec(line);
    if (fence) {
      const close = fence[1]!;
      const body: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.trim().startsWith(close)) body.push(lines[i++]!);
      i++;
      out.push({ kind: "code", lang: fence[2] ?? "", text: body.join("\n") });
      continue;
    }
    const h = headingRe.exec(line);
    if (h) {
      out.push({ kind: "h", level: h[1]!.length, text: h[2]! });
      i++;
      continue;
    }
    if (ruleRe.test(line)) {
      out.push({ kind: "hr" });
      i++;
      continue;
    }
    if (quoteRe.test(line)) {
      const body: string[] = [];
      while (i < lines.length && lines[i]!.trim() !== "" && quoteRe.test(lines[i]!)) body.push(quoteRe.exec(lines[i++]!)![1]!);
      out.push({ kind: "quote", blocks: blocks(body.join("\n")) });
      continue;
    }
    const next = lines[i + 1];
    if (next !== undefined && isTable(line, next)) {
      const head = cells(line);
      const align: Align[] = cells(next).map((c) => (c.startsWith(":") && c.endsWith(":") ? "center" : c.endsWith(":") ? "right" : c.startsWith(":") ? "left" : null));
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i]!.trim() !== "" && lines[i]!.includes("|")) rows.push(cells(lines[i++]!));
      out.push({ kind: "table", align, head, rows });
      continue;
    }
    const item = itemRe.exec(line);
    if (item) {
      i = list(lines, i, out);
      continue;
    }
    // a paragraph: up to a blank line or the start of another block
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() !== "" && (para.length === 0 || !startsBlock(lines[i]!, lines[i + 1]))) para.push(lines[i++]!);
    out.push({ kind: "p", text: para.join("\n").trim() });
  }
  return out;
}

/** A list from line `at`: its items, each with what is indented under it read as blocks of its own. */
function list(lines: string[], at: number, out: Block[]): number {
  const first = itemRe.exec(lines[at]!)!;
  const indent = first[1]!.length;
  const ordered = /\d/.test(first[2]!);
  const items: { task?: boolean; blocks: Block[] }[] = [];
  const same = (l: string | undefined) => {
    // a rule ("* * *", "- - -") right after an item ends the list, not another item
    const m = l === undefined || ruleRe.test(l) ? null : itemRe.exec(l);
    return m !== null && m[1]!.length === indent && /\d/.test(m[2]!) === ordered ? m : null;
  };
  let i = at;
  while (i < lines.length) {
    // a loose list, its items a blank line apart, as agents often write
    // one, is still one list: the blank lines before its next item are skipped
    let j = i;
    while (j < lines.length && lines[j]!.trim() === "") j++;
    if (j > i && same(lines[j])) i = j;
    const m = same(lines[i]);
    if (!m) break;
    const body = [m[3]!];
    const inner = indent + m[2]!.length + 1;
    i++;
    // what belongs to the item: lines indented past its marker, and blank
    // lines between them when the item goes on after one
    while (i < lines.length) {
      const l = lines[i]!;
      if (l.trim() === "") {
        const after = lines[i + 1];
        if (after !== undefined && after.trim() !== "" && after.length - after.trimStart().length >= inner) {
          body.push("");
          i++;
          continue;
        }
        break;
      }
      const lead = l.length - l.trimStart().length;
      if (lead > indent) body.push(l.slice(Math.min(lead, inner)));
      else if (!itemRe.test(l) && !startsBlock(l, lines[i + 1])) body.push(l.trimStart());
      else break;
      i++;
    }
    const task = /^\[([ xX])\]\s+/.exec(body[0]!);
    if (task) body[0] = body[0]!.slice(task[0].length);
    items.push({ ...(task ? { task: task[1] !== " " } : {}), blocks: blocks(body.join("\n")) });
  }
  out.push({ kind: "list", ordered, start: ordered ? parseInt(first[2]!, 10) : 1, items });
  return i;
}

const safeHref = (url: string) => (/^(https?:|mailto:)/i.test(url) ? url : null);
const word = (c: string | undefined) => c !== undefined && /[\p{L}\p{N}]/u.test(c);

// code spans first, then links, then emphasis, then a bare URL
const inlineSrc = /(`+)([^`]|[^`][\s\S]*?[^`])\1(?!`)|\[([^\]\n]+)\]\(([^)\s]+)\)|(\*\*|__)(?=\S)([\s\S]*?\S)\5|~~(?=\S)([\s\S]*?\S)~~|([*_])(?=\S)([^*_\n]*?[^\s*_])\8|(https?:\/\/[^\s<>()]*[^\s<>().,;:!?'"])/gu;

/** `inLink`: inside a link's text, where a bare URL stays text — a link in a link is no link. */
export function inline(text: string, key = "i", inLink = false): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  // one of its own: what is inside bold or a link is read by a call of its
  // own, which would otherwise move this one's place in the text
  const inlineRe = new RegExp(inlineSrc);
  for (let m = inlineRe.exec(text); m; m = inlineRe.exec(text)) {
    const at = m.index;
    const end = at + m[0].length;
    // an underscore inside a word (snake_case) is not emphasis
    const delim = m[5] ?? m[8];
    if (delim?.startsWith("_") && (word(text[at - 1]) || word(text[end]))) {
      inlineRe.lastIndex = at + 1;
      continue;
    }
    if (at > last) out.push(text.slice(last, at));
    const k = `${key}-${n++}`;
    if (m[1] !== undefined) {
      out.push(<code key={k}>{m[2]!.trim() || m[2]}</code>);
    } else if (m[3] !== undefined) {
      const href = safeHref(m[4]!);
      out.push(
        href ? (
          <a key={k} href={href} target="_blank" rel="noopener noreferrer nofollow">
            {inline(m[3], k, true)}
          </a>
        ) : (
          <span key={k}>{inline(m[3], k, inLink)}</span>
        ),
      );
    } else if (m[5] !== undefined) {
      out.push(<strong key={k}>{inline(m[6]!, k, inLink)}</strong>);
    } else if (m[7] !== undefined) {
      out.push(<del key={k}>{inline(m[7], k, inLink)}</del>);
    } else if (m[8] !== undefined) {
      out.push(<em key={k}>{inline(m[9]!, k, inLink)}</em>);
    } else if (inLink) {
      out.push(m[10]);
    } else {
      out.push(
        <a key={k} href={m[10]} target="_blank" rel="noopener noreferrer nofollow">
          {m[10]}
        </a>,
      );
    }
    last = end;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Blocks({ list, prefix }: { list: Block[]; prefix: string }) {
  return (
    <>
      {list.map((b, i) => {
        const k = `${prefix}${i}`;
        switch (b.kind) {
          case "code":
            return (
              <pre key={k} className="code" data-lang={b.lang || undefined}>
                <code>{b.text}</code>
              </pre>
            );
          case "h": {
            // an answer's headings sit under the turn's: never larger than a sub-heading
            const H = `h${Math.min(6, b.level + 2)}` as "h3";
            return (
              <H key={k} className="md-h">
                {inline(b.text, k)}
              </H>
            );
          }
          case "hr":
            return <hr key={k} />;
          case "quote":
            return (
              <blockquote key={k}>
                <Blocks list={b.blocks} prefix={`${k}.`} />
              </blockquote>
            );
          case "list": {
            const items = b.items.map((it, j) => (
              <li key={j} className={it.task !== undefined ? "md-task" : undefined}>
                {it.task !== undefined && <input type="checkbox" checked={it.task} disabled aria-label={it.task ? "done" : "to do"} />}
                <Blocks list={it.blocks} prefix={`${k}.${j}.`} />
              </li>
            ));
            return b.ordered ? (
              <ol key={k} start={b.start === 1 ? undefined : b.start}>
                {items}
              </ol>
            ) : (
              <ul key={k}>{items}</ul>
            );
          }
          case "table":
            return (
              <div key={k} className="md-table">
                <table>
                  <thead>
                    <tr>
                      {b.head.map((c, j) => (
                        <th key={j} style={b.align[j] ? { textAlign: b.align[j]! } : undefined}>
                          {inline(c, `${k}h${j}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((r, j) => (
                      <tr key={j}>
                        {b.head.map((_, c) => (
                          <td key={c} style={b.align[c] ? { textAlign: b.align[c]! } : undefined}>
                            {inline(r[c] ?? "", `${k}r${j}c${c}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "p":
            return <p key={k}>{inline(b.text, k)}</p>;
        }
      })}
    </>
  );
}

/**
 * An answer, read once: the timeline draws again on every badge pressed and
 * every poll for threads, and an answer that has not changed is not parsed
 * again for it.
 */
export const Markdown = memo(function Markdown({ text }: { text: string }) {
  const list = useMemo(() => blocks(text), [text]);
  return (
    <div className="md">
      <Blocks list={list} prefix="b" />
    </div>
  );
});
